import { getMarkets } from "@dotcom/markets";
import { getAlternateLinks, getPageMeta, mergeMeta } from "@dotcom/remix-meta";
import { Disclaimer, useDeviceFromUrl } from "@dotcom/ui";
import { Spacer, Spacings } from "@polestar/component-warehouse-react";
import { json, type LoaderFunctionArgs } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";
import { promiseHash } from "remix-utils/promise";

import {
  Breadcrumbs,
  Intro,
  NewsShareButton,
  RelatedNewsArticles,
  Sections,
} from "./components";
import {
  getNewsOverviewTitle,
  getRelatedNewsArticles,
  getShareButton,
} from "./data";

import { getErrorPageData, getNewsArticleBySlug } from "~/common/data";
import { getIsChina } from "~/common/helpers/.server";
import getNewsArticleMeta from "~/common/helpers/getNewsArticleStructuredData/getNewsArticleMeta";
import { type RootLoader } from "~/root";

export const meta = mergeMeta<typeof loader, { root: RootLoader }>(
  ({ data, location }) => {
    if (!data) return [];

    const {
      seo,
      hasNoRobotMeta,
      publishedMarkets,
      heroSections,
      publishDate,
      modifiedAt,
    } = data.article;

    return [
      ...getNewsArticleMeta({
        headline: data.article?.headline,
        publishDate,
        modifiedDate: modifiedAt,
        heroSection: heroSections?.[0],
      }),
      ...getPageMeta({
        title: seo?.title,
        description: seo?.description,
        image: seo?.image?.url,
        twitterCard: seo?.twitterCard,
        noIndex: hasNoRobotMeta,
      }),
      ...getAlternateLinks(location.pathname, publishedMarkets, data.isChina),
    ];
  }
);

export const loader = async ({
  params,
  context: { market, datoClient },
  request,
}: LoaderFunctionArgs) => {
  if (!market) {
    throw json(
      await getErrorPageData(datoClient, { requestUrl: request.url }),
      { status: 404 }
    );
  }

  const article = await getNewsArticleBySlug(datoClient, {
    slug: String(params.slug),
    market,
  });

  if (!article) {
    throw json(
      await getErrorPageData(datoClient, {
        requestUrl: request.url,
        market,
      }),
      { status: 404 }
    );
  }
  const isChina = getIsChina();
  return {
    isChina,
    market,
    markets: getMarkets(datoClient.isPreview, isChina),
    article,
    ...(await promiseHash({
      overviewTitle: getNewsOverviewTitle(datoClient),
      shareButton: getShareButton(datoClient),
      relatedArticles: getRelatedNewsArticles(datoClient, {
        idToIgnore: article?.id,
        tagIds:
          article?.tags?.map((tag) => {
            return tag.id;
          }) ?? [],
        market,
      }),
      dictionary: datoClient.getFromDictionary([
        "Email",
        "Related",
        "Read more",
      ]),
    })),
  };
};

const NewsArticlePage = () => {
  const {
    overviewTitle,
    article,
    relatedArticles,
    shareButton,
    dictionary,
    market,
    isChina,
  } = useLoaderData<typeof loader>();

  const device = useDeviceFromUrl();
  const showShareButton = !device;

  return (
    <>
      <Spacer spacing={{ mobile: Spacings.xLarge, tablet: Spacings.xxLarge }} />

      <Breadcrumbs
        links={[
          { href: `/${market.locale}/news`, label: overviewTitle },
          { href: "#", label: article.headline },
        ]}
      />

      <Intro
        localizedDate={article.localizedPublishDate}
        headline={article.headline}
        ingress={article.ingress}
      />

      <Sections
        sections={[
          ...(article.heroSections ?? []),
          ...(article.contentSections ?? []),
        ]}
        market={market}
        isChina={isChina}
      />

      {showShareButton && (
        <NewsShareButton
          emailLabel={dictionary.Email ?? "Email"}
          copyLabel={shareButton.copyLabel}
          buttonLabel={shareButton.title}
        />
      )}

      {article.disclaimer && <Disclaimer text={article.disclaimer} />}

      <RelatedNewsArticles
        locale={market.locale}
        title={dictionary.Related ?? "Related"}
        articles={relatedArticles}
        readMoreLabel={dictionary["Read more"] ?? "Read more"}
      />
    </>
  );
};

export default NewsArticlePage;
